import {
  appendQueryParameters,
  buildUpdateProjectMilestonePayload,
  fetchBaseQueryWrapper,
  NO_CACHING,
  projectCleaner,
} from "@/services/helpers";
import { ISku } from "@/interfaces";
import { createApi } from "@reduxjs/toolkit/query/react";

const TAG_TYPES = {
  project: "Project",
  projectStatus: "Project Status",
  comments: "Comments",
};

export const projectApi = createApi({
  reducerPath: "projectApi",
  baseQuery: fetchBaseQueryWrapper({
    path: "projects",
  }),
  tagTypes: [TAG_TYPES.project, TAG_TYPES.projectStatus, TAG_TYPES.comments],
  keepUnusedDataFor: NO_CACHING,
  endpoints: (build) => ({
    saveProject: build.mutation({
      query: (project) => ({
        url: "edit",
        method: "PUT",
        body: project,
      }),
      invalidatesTags: [TAG_TYPES.project],
    }),
    saveProjectIntake: build.mutation({
      query: (projectIntake) => ({
        url: "intake",
        method: "POST",
        body: projectIntake,
      }),
    }),
    updateProjectIntake: build.mutation({
      query: ({ id, changes }) => ({
        url: `intake/${id}`,
        method: "PATCH",
        body: changes,
      }),
    }),
    submitProjectIntake: build.mutation({
      query: (projectIntake) => ({
        url: "intakeSubmit",
        method: "POST",
        body: projectIntake,
      }),
      transformResponse: ({ data }) => data,
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getProject: build.query<any, any>({
      query: (id) => id,
      transformResponse: ({ data }) => projectCleaner(data),
      providesTags: [TAG_TYPES.project],
    }),
    deleteProject: build.mutation({
      query: (id) => ({
        url: id,
        actionPayload: { id },
        method: "DELETE",
      }),
    }),
    searchProjects: build.query({
      query: (queryParams) => appendQueryParameters("", queryParams),
    }),
    updateProjectSku: build.mutation<
      ISku[],
      { projectId: string; skus: ISku[] }
    >({
      query: ({ projectId, skus }) => ({
        url: `${projectId}/sku`,
        method: "POST",
        body: skus,
      }),
      transformResponse: ({ data }: { data: ISku[] }) => data,
    }),
    updateProjectSchedules: build.mutation({
      query: ({ projectId, schedulesToCreate, schedulesToUpdate, scheduleIdsToDelete }) => ({
        url: `${projectId}/schedules`,
        method: "POST",
        body: {
          schedulesToCreate,
          schedulesToUpdate,
          scheduleIdsToDelete,
        },
      }),
      transformResponse: ({ data }) => data,
    }),
    updateProjectLeaders: build.mutation({
      query: ({ id, regionalProjectLeaders, backupProjectLeader }) => ({
        url: `${id}/projectLeaders`,
        method: "POST",
        body: {
          projectLeaders: regionalProjectLeaders,
          backupProjectLeader,
        },
      }),
      transformResponse: ({ data }) => data,
      invalidatesTags: [TAG_TYPES.projectStatus, TAG_TYPES.project],
    }),
    updateProjectTeam: build.mutation({
      query: ({ id, projectTeam }) => ({
        url: `${id}/projectTeam`,
        method: "POST",
        body: projectTeam,
      }),
      transformResponse: ({ data }) => data,
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: project } = await queryFulfilled;
          dispatch(
            projectApi.util.updateQueryData("getProject", id, () => project)
          );
        } catch {
          console.error(
            "error updating project query cache during updateProjectTeam"
          );
        }
      },
    }),
    updateProjectStatusKpi: build.mutation({
      query: ({ projectId, kpi, status }) => ({
        url: `/kpiStatusUpdate/${projectId}`,
        method: "PATCH",
        body: {
          KPI: kpi,
          Status: status,
        },
      }),
      transformResponse: ({ data }) => data,
    }),
    checkTeamsAccess: build.query({
      query: (id) => `${id}/checkUserTeamsAccess`,
      transformResponse: ({ data }) => data,
    }),
    addProjectMilestones: build.mutation({
      query: ({
        projectId,
        parentId,
        targetSchedule,
        fromTemplate,
        fromProject,
      }) => ({
        url: `${projectId}/milestone/add`,
        method: "POST",
        body: {
          ParentId: parentId,
          IncludeChildren: !parentId,
          TargetSchedule: targetSchedule,
          ProjectTemplateMilestones: fromTemplate,
          ProjectMilestones: fromProject,
        },
      }),
      transformResponse: ({ data }) => data,
    }),
    changeMilestoneStatus: build.mutation({
      query: ({ projectId, milestoneId, statusAction, comment }) => ({
        url: `${projectId}/milestone/status`,
        method: "POST",
        body: {
          MilestoneId: milestoneId,
          Action: statusAction,
          Comment: comment,
        },
      }),
      invalidatesTags: [TAG_TYPES.comments, TAG_TYPES.projectStatus],
    }),
    updateProjectMilestone: build.mutation({
      query: ({ projectId, milestone }) => ({
        url: `${projectId}/milestone`,
        method: "PATCH",
        body: buildUpdateProjectMilestonePayload(milestone),
      }),
      transformResponse: ({ data }) => data,
    }),
    notifyMilestoneApprovers: build.mutation({
      query: ({ projectId, milestoneId, approversToNotify }) => ({
        url: `${projectId}/milestone/notifyApprovers`,
        method: "POST",
        body: {
          MilestoneId: milestoneId,
          Approvers: approversToNotify,
        },
      }),
    }),
    deleteProjectMilestone: build.mutation({
      query: ({ projectId, milestoneId }) => ({
        url: `${projectId}/milestone/${milestoneId}`,
        method: "DELETE",
      }),
      transformResponse: ({ data }) => data,
    }),
    updateProjectMilestoneTargetDate: build.mutation({
      query: ({ projectId, milestoneId, targetDate }) => ({
        url: `${projectId}/milestone/${milestoneId}/targetDate`,
        method: "PATCH",
        body: {
          Date: targetDate,
        },
      }),
      transformResponse: ({ data }) => data,
    }),
    updateProjectMilestoneActualDate: build.mutation({
      query: ({ projectId, milestoneId, actualDate }) => ({
        url: `${projectId}/milestone/${milestoneId}/actualDate`,
        method: "PATCH",
        body: {
          Date: actualDate,
        },
      }),
      transformResponse: ({ data }) => data,
    }),
    getTemplateRolesMapping: build.query({
      query: (projectId) => `${projectId}/templateRolesMapping`,
      transformResponse: ({ data }) => data,
    }),
    getStatuses: build.query({
      query: (projectId) => `status/${projectId}`,
      transformResponse: ({ data }) => data,
      providesTags: [TAG_TYPES.projectStatus],
    }),
    changeStatus: build.mutation({
      query: ({ projectId, statusId }) => ({
        url: `${projectId}/status/${statusId}`,
        method: "PUT",
      }),
      transformResponse: ({ data }) => data,
      invalidatesTags: [TAG_TYPES.projectStatus, TAG_TYPES.project],
    }),
    getComments: build.query({
      query: ({ projectId, target, targetId }) =>
        `${projectId}/${target}/${targetId}/comments`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformResponse: (response: { data: any }) => response.data,
      providesTags: [TAG_TYPES.comments],
    }),
    createComment: build.mutation({
      query: ({ routeParams, body }) => ({
        url: `${routeParams.projectId}/${routeParams.target}/${routeParams.targetId}/comments`,
        method: "POST",
        body: body,
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformResponse: (response: { data: any }) => response.data,
      async onQueryStarted({ routeParams }, { dispatch, queryFulfilled }) {
        try {
          const { data: addedComment } = await queryFulfilled;
          const commentKey = {
            projectId: routeParams.projectId,
            target: routeParams.target,
            targetId: routeParams.targetId,
          };
          dispatch(
            projectApi.util.updateQueryData(
              "getComments",
              commentKey,
              (draft) => {
                draft.comments.unshift(addedComment);
                draft.commentsCount++;
              }
            )
          );
        } catch {
          console.error(
            "error updating comment query cache during new comment"
          );
        }
      },
    }),
    createReply: build.mutation({
      query: ({
        routeParams: { projectId, target, targetId, commentId },
        body,
      }) => ({
        url: `${projectId}/${target}/${targetId}/comments/${commentId}/reply`,
        method: "PATCH",
        body: body,
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformResponse: (response: { data: any }) => response.data,
      async onQueryStarted({ routeParams }, { dispatch, queryFulfilled }) {
        try {
          const { data: addedReply } = await queryFulfilled;
          const commentKey = {
            projectId: routeParams.projectId,
            target: routeParams.target,
            targetId: routeParams.targetId,
          };
          dispatch(
            projectApi.util.updateQueryData(
              "getComments",
              commentKey,
              (draft) => {
                const updatedCommentIndex = draft.comments.findIndex(
                  (comment: { id: string }) => comment.id === routeParams.commentId
                );
                draft.comments[updatedCommentIndex] = addedReply;
                draft.commentsCount++;
              }
            )
          );
        } catch {
          console.error("error updating comment query cache during reply");
        }
      },
    })
  }),
});

export const {
  useSaveProjectMutation,
  useSaveProjectIntakeMutation,
  useUpdateProjectIntakeMutation,
  useSubmitProjectIntakeMutation,
  useGetProjectQuery,
  useDeleteProjectMutation,
  useSearchProjectsQuery,
  useUpdateProjectSkuMutation,
  useUpdateProjectSchedulesMutation,
  useUpdateProjectLeadersMutation,
  useUpdateProjectTeamMutation,
  useUpdateProjectStatusKpiMutation,
  useCheckTeamsAccessQuery,
  useAddProjectMilestonesMutation,
  useChangeMilestoneStatusMutation,
  useUpdateProjectMilestoneMutation,
  useDeleteProjectMilestoneMutation,
  useUpdateProjectMilestoneTargetDateMutation,
  useUpdateProjectMilestoneActualDateMutation,
  useGetTemplateRolesMappingQuery,
  useGetStatusesQuery,
  useChangeStatusMutation,
  useGetCommentsQuery,
  useCreateCommentMutation,
  useCreateReplyMutation,
  useNotifyMilestoneApproversMutation,
} = projectApi;
